import { Component, Input, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { DomainService } from 'app/services/entities/domain/domain.service';
import { FormDomainScriptGuideService } from './form-domain-script-guide.service';
import { FormDomainScriptGuideHeatmapsService } from './features/heatmaps/service/heatmaps.service';
import { FormDomainScriptGuidePageFixService } from './features/page-fix/service/page-fix.service';
import { FormDomainScriptGuideStatisticsService } from './features/statistics/statistics.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { FormDomainScriptPageAssistService } from './models/page-assist/form-domain-script-page-assist.service';
import { FormDomainScriptGuidePageAssistService } from './features/page-assist/form-domain-script-guide-page-assist.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { CookieBannerHelperService } from '@client/app/modules/cookie-banner/services/helper/helper.service';
import { FormDomainScriptCookieBannerService } from './models/cookie-banner/form-domain-script-cookie-banner.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DemoHasAccessService } from 'app/modules/demodata/services/demo-has-access.service';
import { FormUtils } from '@client/ng2/shared/form-utils';
import { NgForm } from '@angular/forms';
import { FormDomainScriptGuideLegacyComponent } from './legacy/legacy.component';
import { domainScriptUrl, domainScriptConsentManagerUrl } from '@monsido/core/constants/general.constant';

import { safeClone } from '@client/app/shared/utils/object-utils';
@Component({
    selector: 'form-domain-script-guide',
    standalone: false,
    templateUrl: './domain-script-guide.component.html',
})
export class FormDomainScriptGuideComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() domain: any;
    @Input() monDialogId: string = '';
    @Input() setForm: any;

    @ViewChild('domainScriptSetupForm') domainScriptSetupForm?: NgForm;

    // Component state
    loading: boolean = false;
    saving: boolean = false;
    formUnsaved: boolean = false;
    showScript: boolean = false;
    saveButtonDisabled: boolean = true;
    canUseCB: boolean = false;
    isDemo: boolean = false;
    id: string = '';

    // Models
    pageAssistModel: any;
    cookieBannerModel: any;
    originalSettings: any;

    // Event subscription
    private eventId: any;

    constructor (
        private domainService: DomainService,
        private formDomainScriptGuideService: FormDomainScriptGuideService,
        private formDomainScriptGuideHeatmapsService: FormDomainScriptGuideHeatmapsService,
        private formDomainScriptGuidePageFixService: FormDomainScriptGuidePageFixService,
        private formDomainScriptGuideStatisticsService: FormDomainScriptGuideStatisticsService,
        private monDialog: MonDialog,
        private formDomainScriptPageAssistService: FormDomainScriptPageAssistService,
        private formDomainScriptGuidePageAssistService: FormDomainScriptGuidePageAssistService,
        private monEventsService: MonEventService,
        private cookieBannerHelperService: CookieBannerHelperService,
        private formDomainScriptCookieBannerService: FormDomainScriptCookieBannerService,
        private sessionService: SessionService,
        private demoHasAccessService: DemoHasAccessService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    ngAfterViewInit () {
        if (this.setForm) {
            this.setForm({ id: this.monDialogId, form: this.domainScriptSetupForm });
        }
    }

    ngOnDestroy () {
        this.onDestroy();
    }

    activate () {
        this.formDomainScriptGuideService.showScriptObs.subscribe((val: boolean) => {
            this.showScript = val;
        });
        this.isDemo = this.demoHasAccessService.isDemoConsentManager();
        this.saving = false;
        this.id = 'script' + Math.floor(Math.random() * 100);
        this.formDomainScriptGuideService.setDomain(this.domain);

        const pageAssistSettings = this.domain.settings ? this.domain.settings.scripts.page_assist_v2 : {};
        const pageAssistLegacySettings = this.domain.settings ? this.domain.settings.scripts.page_assist : {};
        this.pageAssistModel = this.formDomainScriptPageAssistService.formDomainScriptPageAssistModel(pageAssistSettings, pageAssistLegacySettings);

        const consentManagerInitObj = { enabled: false };
        if (this.domain.settings.scripts.cookie_banner && this.domain.settings.scripts.cookie_banner.enabled) {
            Object.assign(consentManagerInitObj, this.domain.settings.scripts.cookie_banner);
        }
        this.cookieBannerModel = this.formDomainScriptCookieBannerService.getFormDomainScriptCookieBannerModel(consentManagerInitObj);

        this.getDomain().then(() => {
            setTimeout(() => {
                if (this.setForm) {
                    this.setForm({ id: this.monDialogId, form: this.domainScriptSetupForm });
                }
            });
            this.canUseCB = !!this.sessionService.customer?.plan_traits.cookie_banner;
            this.saveButtonDisabled = !this.canSave();
        });
        this.formUnsaved = false;

        this.addEventListener();
    }

    openLegacy () {
        const params = {
            body: FormDomainScriptGuideLegacyComponent,
            size: 'sm',
            data: {
                domain: this.domain,
                pageAssistModel: this.pageAssistModel,
            },
        };

        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    getScript () {
        let script = '&lt;script type="text/javascript"&gt; <br>';
        script += this.formDomainScriptGuideService.tab() + 'window._monsido = window._monsido || {<br>';
        script += this.formDomainScriptGuideService.getParameters(this.domain, this.pageAssistModel, {
            statistics: {
                value: this.formDomainScriptGuideStatisticsService.getValue(),
                cookieLessTracking: this.formDomainScriptGuideStatisticsService.getCookielessTracking(),
                documentTracking: this.formDomainScriptGuideStatisticsService.getDocumentTracking(),
                documentClass: this.formDomainScriptGuideStatisticsService.getDocumentClass(),
                documentIgnoreClass: this.formDomainScriptGuideStatisticsService.getDocumentIgnoreClass(),
                filledDocumentExtensions: this.formDomainScriptGuideStatisticsService.getFilledDocumentExtensions(),
            },
            heatmaps: {
                value: this.formDomainScriptGuideHeatmapsService.getValue(),
            },
            pageAssist: {
                accessToDomain: this.formDomainScriptGuidePageAssistService.getAccessToFeature(this.domain),
            },
            pageFix: {
                value: this.formDomainScriptGuidePageFixService.getValue(),
            },
        });
        script += this.formDomainScriptGuideService.tab() + '};<br>';

        if (this.cookieBannerModel.settings.enabled) {
            if (this.isDemo) {
                script +=
                    '<span class="blurred-line width-one"></span><br>' +
                    '<span class="blurred-line width-one indent-one"></span><br>' +
                    '<span class="blurred-line indent-two"></span><br>' +
                    '<span class="blurred-line width-two indent-one"></span><br>' +
                    '<span class="blurred-line indent-two"></span><br>' +
                    '<span class="blurred-line width-one indent-two"></span><br>' +
                    '<span class="blurred-line indent-one"></span><br>' +
                    '<span class="blurred-line width-one"></span><br>' +
                    '<span class="blurred-line width-two indent-one"></span><br>' +
                    '<span class="blurred-line indent-one"></span><br>' +
                    '<span class="blurred-line width-one indent-two"></span><br>' +
                    '<span class="blurred-line indent-one"></span><br>' +
                    '<span class="blurred-line indent-one"></span><br>' +
                    '<span class="blurred-line"></span><br>';
            } else {
                script +=
                    this.formDomainScriptGuideService.tab() +
                    'window._monsidoConsentManagerConfig = window._monsidoConsentManagerConfig || {<br>';
                script += this.formDomainScriptGuideService.getCookieBannerParameters(this.domain, this.cookieBannerModel, {
                    cookieBanner: {
                        accessToDomain: this.cookieBannerHelperService.isAbleToSetupScript(this.domain),
                    },
                });
            }
        }
        if (!this.isDemo) {
            script += '&lt;/script&gt;<br>';
            script += this.getScriptSrc() + '<br>';
            script += this.getConsentManagerScriptSrc();
        }
        this.saveButtonDisabled = !this.canSave();
        return script;
    }

    submit () {
        this.formDomainScriptGuideService.setPageAssistEdit(this.pageAssistModel); // Ensure that PageAssist settings is set on submit
        this.formDomainScriptGuideService.setCookieBannerEdit(this.cookieBannerModel);
        this.saving = true;
        const isAdmin = this.sessionService.hasRole('customer_admin');
        const isCMEnable = this.cookieBannerModel.settings.enabled;
        const isFirstTimeUseCM = this.cookieBannerModel.settings.firstTimeEnableCM;
        const linkForOverview = this.cookieBannerModel.settings.cookieOverviewLinkEnabled;
        const pageHref = 'base.customer.domain.data_privacy.consent_overview.cookie_overview';

        this.domainService
            .update(this.domain.id, {
                settings: {
                    scripts: this.formDomainScriptGuideService.getDomainSettings() as any,
                },
            })
            .then(() => {
                return this.runGetDomain().then(
                    (domain) => this.setDomain(domain),
                    () => {},
                );
            })
            .then(() => {
                FormUtils.markFormAsPristine(this.domainScriptSetupForm);
            })
            .then(() => {
                this.monDialog.close(this.monDialogId, this.pageAssistModel);
            })
            .finally(() => {
                this.saving = false;
                if (isAdmin && isCMEnable && isFirstTimeUseCM && !linkForOverview) {
                    this.formDomainScriptGuideService.confirmOpenCookieOverviewPagePopup(this.domain.id, pageHref);
                }
            });
    }

    updatePageAssistSettings (settings: any) {
        this.formDomainScriptGuideService.setScriptSettingBlock('page_assist', settings, true);
    }

    setScriptUnsaved () {
        if (this.domainScriptSetupForm) {
            this.domainScriptSetupForm.form.markAsDirty();
        }
        this.formUnsaved = true;
    }

    // PROTECTED
    private getDomain () {
        this.loading = true;

        return this.runGetDomain()
            .then(
                (domain) => this.setDomain(domain),
                () => {},
            )
            .finally(() => {
                this.loading = false;
            });
    }

    private setFeatureDomainSettings () {
        this.formDomainScriptGuideHeatmapsService.setValue(this.domain.settings.scripts.heatmap.enabled, false);
        this.formDomainScriptGuidePageFixService.setValue(this.domain.settings.scripts.page_correct.enabled);
        this.formDomainScriptGuideStatisticsService.setValue(this.domain.settings.scripts.statistics.enabled);
        this.formDomainScriptGuideStatisticsService.setCookielessTracking(this.domain.settings.scripts.statistics.cookieless_tracking);
        this.formDomainScriptGuideStatisticsService.setDocumentTracking(this.domain.settings.scripts.statistics.document_tracking.enabled);
        this.formDomainScriptGuideStatisticsService.setDocumentExtensions(
            this.domain.settings.scripts.statistics.document_tracking.document_ext,
        );
        this.formDomainScriptGuideStatisticsService.setDocumentClass(this.domain.settings.scripts.statistics.document_tracking.document_cls);
        this.formDomainScriptGuideStatisticsService.setDocumentIgnoreClass(
            this.domain.settings.scripts.statistics.document_tracking.document_ignore_cls,
        );
    }

    private getScriptSrc () {
        return '&lt;script type="text/javascript" async src="' + domainScriptUrl + '"&gt;&lt;/script&gt;';
    }

    private getConsentManagerScriptSrc () {
        if (this.cookieBannerModel.settings.enabled && this.cookieBannerHelperService.isAbleToSetupScript(this.domain)) {
            return '&lt;script type="text/javascript" src="' + domainScriptConsentManagerUrl + '"&gt;&lt;/script&gt;';
        }
        return '';
    }

    private runGetDomain () {
        return this.domainService.get(this.domain.id);
    }

    private setDomain (domain: any) {
        this.originalSettings = safeClone(domain.settings.scripts.page_assist_v2);
        this.domain = domain;
        this.pageAssistModel.setLegacySettings(domain.settings.scripts.page_assist);
        this.pageAssistModel.setSettings(domain.settings.scripts.page_assist_v2);
        this.cookieBannerModel.setSettings(domain.settings.scripts.cookie_banner);
        this.formDomainScriptGuideService.setDomain(domain);
        this.setFeatureDomainSettings();
    }

    private canSave () {
        return (
            !this.saving &&
            (!this.pageAssistModel.settings.enabled || this.pageAssistModel.settings.canSavePaSetting) &&
            (!this.cookieBannerModel.settings.enabled || this.cookieBannerModel.canSaveModel())
        );
    }

    private addEventListener () {
        this.eventId = this.monEventsService.addListener('pageAssistScriptSettingsUpdate' as any, () => {
            this.pageAssistModel.settings = this.formDomainScriptGuideService.getDomainSettings().page_assist_v2;
            this.originalSettings = this.pageAssistModel.settings;
        });
    }

    private onDestroy () {
        this.monEventsService.remove(this.eventId);
    }
}
